import { useState, useEffect, useCallback } from "react";
import { visualSliderData } from "@/mocks/homeData";
import AnimatedSection from "@/components/AnimatedSection";

export default function VisualSlider() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const slides = visualSliderData.slides;

  const nextSlide = useCallback(() => {
    setActiveSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setActiveSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goToSlide = useCallback((index: number) => {
    setActiveSlide(index);
  }, []);

  // Auto-play every 5 seconds
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);
    return () => clearInterval(interval);
  }, [isPaused, nextSlide]);

  // Keyboard accessibility
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prevSlide();
      if (e.key === "ArrowRight") nextSlide();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextSlide, prevSlide]);

  const slide = slides[activeSlide];

  return (
    <section
      className="py-16 md:py-24 bg-background-50 relative overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="container-astro relative z-10">
        <AnimatedSection>
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-foreground-950 mb-3 font-heading tracking-tight">
              Software Solutions Built for Business Growth
            </h2>
            <div className="w-16 h-1 rounded-full mx-auto bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316]" />
          </div>
        </AnimatedSection>

        <AnimatedSection delay={150}>
          <div className="relative rounded-2xl md:rounded-3xl overflow-hidden bg-primary-950 border border-primary-800/40 shadow-2xl shadow-primary-950/20">
            {/* Slide content */}
            <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[420px] md:min-h-[480px]">
              {/* Text side */}
              <div className="flex flex-col justify-center p-8 md:p-12 lg:p-14 order-2 lg:order-1">
                <div
                  key={activeSlide}
                  className="animate-fade-in-up"
                  style={{ animationDuration: "0.5s" }}
                >
                  <h3 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-background-50 mb-4 font-heading tracking-tight leading-tight">
                    {slide.title}
                  </h3>
                  <p className="text-sm md:text-base text-background-400 leading-relaxed mb-8 max-w-md">
                    {slide.text}
                  </p>
                  <a
                    href={slide.ctaHref}
                    className="inline-flex items-center gap-2 px-7 py-3.5 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-sm font-semibold rounded-xl hover:brightness-110 transition-all duration-300 shadow-lg shadow-[#7C3AED]/25 whitespace-nowrap btn-arrow-hover group"
                  >
                    {slide.cta}
                    <i className="ri-arrow-right-line btn-arrow transition-transform duration-300 group-hover:translate-x-1" />
                  </a>
                </div>
              </div>

              {/* Image side */}
              <div className="relative h-56 md:h-72 lg:h-auto order-1 lg:order-2 overflow-hidden">
                {slides.map((s, i) => (
                  <img
                    key={i}
                    src={s.image}
                    alt={s.title}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                      i === activeSlide ? "opacity-100" : "opacity-0"
                    }`}
                    loading={i === 0 ? "eager" : "lazy"}
                  />
                ))}
                {/* Gradient overlay for text readability on mobile */}
                <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/40 to-transparent lg:bg-gradient-to-r lg:from-primary-950 lg:via-primary-950/30 lg:to-transparent" />
              </div>
            </div>

            {/* Bottom controls bar */}
            <div className="flex items-center justify-between px-6 md:px-8 py-4 border-t border-primary-800/40 bg-primary-950/80 backdrop-blur-sm">
              {/* Pagination dots */}
              <div className="flex items-center gap-2">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goToSlide(i)}
                    className={`transition-all duration-300 rounded-full ${
                      i === activeSlide
                        ? "w-8 h-2 bg-gradient-to-r from-[#2563EB] to-[#F97316]"
                        : "w-2 h-2 bg-background-600 hover:bg-background-500"
                    }`}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>

              {/* Arrows */}
              <div className="flex items-center gap-2">
                <button
                  onClick={prevSlide}
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-primary-700/50 text-background-400 hover:text-background-200 hover:border-primary-600 transition-all duration-300"
                  aria-label="Previous slide"
                >
                  <i className="ri-arrow-left-line" />
                </button>
                <button
                  onClick={nextSlide}
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-primary-700/50 text-background-400 hover:text-background-200 hover:border-primary-600 transition-all duration-300"
                  aria-label="Next slide"
                >
                  <i className="ri-arrow-right-line" />
                </button>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}